import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import { Button, BUTTON_TYPES, Loader} from '@livingsecurity/shared';

import * as Styled from './styles';

pdfjs.GlobalWorkerOptions.workerSrc = `//platform-cdn.livingsecurity.com/pkg/pdf.worker.min.js`;

// Mitigation for CVE-2024-4367 in pdfjs-dist 2.12.313: disables the dynamic font
// code path exploited by crafted FontMatrix values. Must be a stable reference so
// react-pdf does not re-fetch the document on every render.
const PDF_DOCUMENT_OPTIONS = { isEvalSupported: false };

const Slideshow = ({ file, onComplete, title, isFinalStep }) => {
  const { t } = useTranslation('contentViewer');
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [srText, setSrText] = useState([]);
  const slideContentRef = useRef(null);
  const announceRef = useRef(null);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  // Hide PDF canvases from screen readers after render
  useEffect(() => {
    const canvases = document.querySelectorAll('.react-pdf__Page__canvas');
    canvases.forEach((canvas) => {
      canvas.setAttribute('aria-hidden', 'true');
      canvas.setAttribute('role', 'presentation');
    });
  }, [pageNumber]);

  // Handle text content extraction for screen readers
  const handleTextSuccess = (textContent) => { 
    const lines = new Map();
    
    textContent.forEach(({ str, transform }) => {
      const y = Math.round(transform[5]); // vertical position
      const existingLine = lines.get(y) || [];
      existingLine.push(str);
      lines.set(y, existingLine);
    });
    
    // Sort by y position (PDF y grows upwards, so reverse sort)
    const orderedLines = [...lines.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([, words]) => words.join(' '))
      .filter(line => line.trim().length > 0); // Remove empty lines

    setSrText(orderedLines);
  };

  function changePage(offset) {
    const newPage = pageNumber + offset;
    setPageNumber(newPage);
    
    // Announce page change to screen readers
    if (announceRef.current) {
      const message = offset > 0 
        ? t('slideshow.page-changed-next', { pageNumber: newPage, numPages })
        : t('slideshow.page-changed-previous', { pageNumber: newPage, numPages });
      announceRef.current.textContent = message;
    }
    
    // Focus management - focus the slide content area after page change
    if (slideContentRef.current) {
      slideContentRef.current.focus();
    }
  }

  // Skip to content functionality
  const skipToContent = () => {
    if (slideContentRef.current) {
      slideContentRef.current.focus();
    }
  };

  return (
    <Styled.SlideshowSection role="region" aria-labelledby="slideshow-title">
      {/* Skip link for accessibility */}
      <Styled.SkipLink onClick={skipToContent}>
        {t('slideshow.skip-to-content') || 'Skip to slide content'}
      </Styled.SkipLink>
      
      {/* Slideshow header with title and context */}
      <Styled.SlideshowHeader>
        <Styled.SlideshowTitle id="slideshow-title">
          {title || (t ? t('slideshow.title') : 'Document Slideshow')}
        </Styled.SlideshowTitle>
      </Styled.SlideshowHeader>

      {/* Main slide content */}
      <Styled.SlideshowMain>
        <Styled.SlideshowContainer
          ref={slideContentRef}
          tabIndex={0}
          role="region"
          aria-label={`${title || 'Document'} - Page ${pageNumber} of ${numPages || 1}`}
          aria-describedby={numPages > 1 ? "page-context" : undefined}
        >
          <Document
            file={file}
            options={PDF_DOCUMENT_OPTIONS}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={console.error}
            loading={<div style={{ marginTop: '100px' }}><Loader /></div>}
            style={{ overflow: 'hidden' }}
            externalLinkTarget="_blank"
          >
            <Styled.Page
              pageNumber={pageNumber}
              width={`${window.innerWidth - 250}`}
              canvasBackground="transparent"
              className="pdf-page"
              loading=""
              renderTextLayer={true}
              renderAnnotationLayer={true}
              onGetTextSuccess={handleTextSuccess}
            />
          </Document>
          
          {/* Screen reader accessible content with actual PDF text */}
          <div 
            className="sr-only"
            style={{ 
              position: 'absolute',
              left: '-10000px',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0
            }}
            role="document"
            aria-label="PDF document content"
          >
            <h2>{title || 'Document Content'}</h2>
            {srText.length > 0 ? (
              srText.map((line, i) => (
                <p key={i}>{line}</p>
              ))
            ) : (
              <p>Loading document content...</p>
            )}
          </div>
        </Styled.SlideshowContainer>
      </Styled.SlideshowMain>

      {/* Navigation controls - placed before content for logical tab order */}
      {numPages > 1 && (
        <Styled.SlideshowNav aria-label={t('slideshow.navigation') || 'Slideshow navigation'}>
          <Styled.NavigationContainer>
            <Button
              variant={BUTTON_TYPES.SKY}
              disabled={pageNumber <= 1}
              onClick={() => changePage(-1)}
              text={t('slideshow.previous')}
              aria-label={t('slideshow.previous-page', { current: pageNumber, total: numPages }) || 
                         `Previous page, currently on page ${pageNumber} of ${numPages}`}
            />
            <Styled.PageIndicator aria-live="polite">
              {t('slideshow.page-counter', { pageNumber, numPages })}
            </Styled.PageIndicator>
            <Button
              variant={BUTTON_TYPES.SKY}
              onClick={pageNumber === numPages ? onComplete : () => changePage(1)}
              text={pageNumber === numPages && isFinalStep ? t('complete') : numPages ? t('continue') : t('slideshow.next')}
              aria-label={pageNumber === numPages 
                ? (t('slideshow.complete-slideshow') || 'Complete slideshow')
                : (t('slideshow.next-page', { current: pageNumber, total: numPages }) || 
                   `Next page, currently on page ${pageNumber} of ${numPages}`)
              }
            />
          </Styled.NavigationContainer>
        </Styled.SlideshowNav>
      )}

      {/* Bottom navigation for single page or completion */}
      {numPages <= 1 && (
        <Styled.SlideshowFooter>
          <Styled.NotificationContainer>
            <Button
              variant={BUTTON_TYPES.SKY}
              onClick={onComplete}
              text={isFinalStep ? t('complete') : t('continue')}
              aria-label={t('slideshow.complete-document') || 'Complete document'}
            />
          </Styled.NotificationContainer>
        </Styled.SlideshowFooter>
      )}

      {/* ARIA live region for page change announcements */}
      <div 
        ref={announceRef}
        aria-live="polite" 
        aria-atomic="true"
        style={{ 
          position: 'absolute', 
          left: '-10000px', 
          width: '1px', 
          height: '1px', 
          overflow: 'hidden' 
        }}
      />
      
      {/* Hidden context for screen readers */}
      {numPages > 1 && (
        <div id="page-context" style={{ position: 'absolute', left: '-10000px' }}>
          {t('slideshow.page-description', { pageNumber, numPages }) || 
           `This is page ${pageNumber} of ${numPages} in the slideshow`}
        </div>
      )}
    </Styled.SlideshowSection>
  );
};

export default Slideshow;
